import React, { useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Shield, Gem, FlaskConical, CheckCircle } from 'lucide-react';

const HeroSection = () => {
  const videoRef = useRef(null);

  useEffect(() => {
    const video = videoRef.current;
    if (video) {
      // Force video to load and play
      video.load();
      video.play().catch(err => {
        console.log('Video autoplay prevented:', err);
      });
    }
  }, []);

  return (
    <section className="relative min-h-[92vh] flex items-center justify-center overflow-hidden pt-28" data-testid="hero-section">
      <div className="absolute inset-0 bg-gradient-to-br from-[#1d0432] via-[#37105a] to-[#130320]" />
      
      {/* Video Background - MP4 for browser compatibility */}
      <video
        ref={videoRef}
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
        className="absolute inset-0 w-full h-full object-cover opacity-50"
        data-testid="hero-video-background"
      >
        {/* Local optimized MP4 first for faster loading */}
        <source src="/hero-video.mp4" type="video/mp4" />
        {/* Fallback to CDN .mov for Safari */}
        <source src="https://customer-assets.emergentagent.com/job_35efb418-d957-4303-979f-4e5863096b08/artifacts/kkprzbrk_amino-chain-peptides.mov" type="video/quicktime" />
      </video>
      
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(217,168,87,0.24),transparent_40%),radial-gradient(circle_at_80%_25%,rgba(189,131,255,0.18),transparent_45%),radial-gradient(circle_at_60%_90%,rgba(244,228,188,0.2),transparent_40%)]" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#1d0432]/80 via-[#2a0b45]/70 to-[#130320]/80" />
      <div className="absolute inset-0 opacity-20" style={{
        backgroundImage: 'linear-gradient(rgba(255,255,255,0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.08) 1px, transparent 1px)',
        backgroundSize: '72px 72px',
      }} />

      <div className="absolute top-20 right-28 w-96 h-96 bg-[#f4e4bc]/20 blur-[140px] rounded-full" />
      <div className="absolute bottom-16 left-10 w-[28rem] h-[28rem] bg-[#8c39c8]/35 blur-[160px] rounded-full" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 py-20">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, x: -48 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#f4e4bc]/15 border border-[#f4e4bc]/45 mb-8"
              data-testid="hero-luxury-badge"
            >
              <span className="w-2.5 h-2.5 rounded-full bg-[#f4e4bc] animate-pulse" />
              <span className="text-[#f9edcf] text-sm font-semibold tracking-wider">SIGNATURE RESEARCH COLLECTION</span>
            </motion.div>

            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-6" data-testid="hero-luxury-heading">
              <span className="text-white">Precision</span>
              <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#f4e4bc] via-[#d6a85a] to-[#f6efda]">
                Synthesized
              </span>
              <br />
              <span className="text-white">Peptides</span>
            </h1>

            <p className="text-[#f7e9cf]/85 text-lg leading-relaxed mb-10 max-w-xl" data-testid="hero-luxury-description">
              Laboratory-grade peptides with <span className="text-[#f4e4bc] font-semibold">&gt;99% verified purity</span>, crafted for researchers who demand uncompromising standards and premium documentation.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/premium-peptides"
                data-testid="hero-shop-btn"
                className="group inline-flex items-center justify-center gap-3 px-8 py-4 bg-gradient-to-r from-[#f4e4bc] to-[#d6a85a] text-[#2f1650] font-heading font-bold uppercase tracking-wider rounded-full hover:shadow-xl hover:shadow-[#f4e4bc]/30 transition-all duration-300"
              >
                Explore Collection
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/peptides-research"
                data-testid="hero-research-btn"
                className="inline-flex items-center justify-center gap-3 px-8 py-4 bg-white/10 border border-[#f4e4bc]/45 text-[#f9edcf] font-heading font-bold uppercase tracking-wider rounded-full hover:bg-white/20 transition-all duration-300"
              >
                Research Library
              </Link>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-12 pt-10 border-t border-[#f4e4bc]/25" data-testid="hero-trust-grid">
              <div className="flex items-center gap-3 px-1 py-1.5 min-h-[76px]" data-testid="hero-trust-purity">
                <div className="w-12 h-12 shrink-0 rounded-xl bg-white/10 border border-[#f4e4bc]/35 flex items-center justify-center">
                  <Shield className="w-7 h-7 text-[#f4e4bc]" strokeWidth={2.1} />
                </div>
                <div className="min-w-0 leading-tight">
                  <p className="text-white font-semibold text-[17px] whitespace-nowrap">99%+ Purity</p>
                  <p className="text-[#eadcbf] text-[13px] whitespace-nowrap">HPLC Verified</p>
                </div>
              </div>
              <div className="flex items-center gap-3 px-1 py-1.5 min-h-[76px]" data-testid="hero-trust-premium">
                <div className="w-12 h-12 shrink-0 rounded-xl bg-white/10 border border-[#f4e4bc]/35 flex items-center justify-center">
                  <Gem className="w-7 h-7 text-[#f4e4bc]" strokeWidth={2.1} />
                </div>
                <div className="min-w-0 leading-tight">
                  <p className="text-white font-semibold text-[17px] whitespace-nowrap">Lab Packaging</p>
                  <p className="text-[#eadcbf] text-[12px] whitespace-nowrap">Single / Half / Full Kits</p>
                </div>
              </div>
              <div className="flex items-center gap-3 px-1 py-1.5 min-h-[76px]" data-testid="hero-trust-coa">
                <div className="w-12 h-12 shrink-0 rounded-xl bg-white/10 border border-[#f4e4bc]/35 flex items-center justify-center">
                  <FlaskConical className="w-7 h-7 text-[#f4e4bc]" strokeWidth={2.1} />
                </div>
                <div className="min-w-0 leading-tight">
                  <p className="text-white font-semibold text-[17px] whitespace-nowrap">COA Included</p>
                  <p className="text-[#eadcbf] text-[12px] whitespace-nowrap">Premium Lab Documentation</p>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="hidden lg:flex items-center justify-center"
          >
            <div className="relative">
              <div className="relative w-80 h-80">
                <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-[#f4e4bc]/25 to-[#7f35bf]/25 blur-2xl" />
                <div className="relative w-full h-full rounded-3xl overflow-hidden border border-[#f4e4bc]/40 bg-white/5 backdrop-blur-sm shadow-2xl">
                  <img
                    src="https://images.unsplash.com/photo-1645839072940-bb2a4f189ed3?auto=format&fit=crop&w=1000&q=80"
                    alt="DNA-inspired peptide backdrop"
                    className="w-full h-full object-cover"
                    data-testid="hero-luxury-image"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#1d0432]/80 to-transparent" />
                  <div className="absolute bottom-4 left-4 right-4">
                    <p className="font-mono text-xs text-[#f4e4bc] mb-1">SIGNATURE BATCH</p>
                    <p className="font-heading text-white font-bold text-lg">DNA-Calibrated Quality</p>
                    <p className="text-[#eadcbf] text-sm">Lab standard, research only</p>
                  </div>
                </div>
              </div>

              <motion.div
                animate={{ y: [0, -9, 0] }}
                transition={{ duration: 3.3, repeat: Infinity, ease: 'easeInOut' }}
                className="absolute -top-4 -right-4 px-4 py-2 bg-white/90 backdrop-blur rounded-xl shadow-lg"
                data-testid="hero-floating-badge-purity"
              >
                <p className="font-mono text-[#5f2a9b] text-sm font-semibold">PURITY 99.8%</p>
              </motion.div>

              <motion.div
                animate={{ y: [0, 10, 0] }}
                transition={{ duration: 4.2, repeat: Infinity, ease: 'easeInOut' }}
                className="absolute -bottom-4 -left-4 px-4 py-2 bg-white/90 backdrop-blur rounded-xl shadow-lg flex items-center gap-2"
                data-testid="hero-floating-badge-lab"
              >
                <CheckCircle className="w-4 h-4 text-green-500" />
                <p className="font-mono text-[#5f2a9b] text-sm font-semibold">LAB VERIFIED</p>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
